// Availability shape helpers — PURE conversions between the profile 7×24
// availability grid and the formalized server-side AvailabilityRule
// (`/core/availability-rules`, WORKSPACE_DATA_MODEL §6.8), plus tz-aware
// summary derivation. No SDK / DOM imports — node-testable
// (availabilityShape.test.js). Persistence lives in availabilityUtils.js.
//
// Grid semantics (matches CalAvailabilityGrid / ProfileAvailabilityGrid):
//   grid[row][hour] — row 0 = Monday … row 6 = Sunday, hour 0-23, boolean.
// Rule semantics (server store): weekly [{ dow, from: 'HH:MM', to: 'HH:MM' }]
// with dow in JS Date#getDay() convention (0 = Sunday … 6 = Saturday).
// `to` is exclusive; a run reaching midnight serializes as to '24:00'.

export const AVAILABILITY_DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const _pad2 = (n) => String(n).padStart(2, '0')
const _hh = (h) => `${_pad2(h)}:00`

// Grid row (0 = Mon) ↔ JS getDay() dow (0 = Sun)
export const gridRowToDow = (row) => (row + 1) % 7
export const dowToGridRow = (dow) => (dow + 6) % 7

export const formatHour = _hh

// 7×24 boolean grid → weekly [{ dow, from, to }] blocks, merging contiguous
// available hours per day. Hour-granular, lossless round-trip with
// weeklyToGrid.
export function gridToWeekly(grid) {
  const weekly = []
  for (let row = 0; row < 7; row++) {
    const cells = (grid && grid[row]) || []
    let start = null
    for (let h = 0; h <= 24; h++) {
      const on = h < 24 && cells[h] === true
      if (on && start == null) start = h
      if (!on && start != null) {
        weekly.push({ dow: gridRowToDow(row), from: _hh(start), to: _hh(h) })
        start = null
      }
    }
  }
  return weekly
}

const _hhmmToMinutes = (s) => {
  if (typeof s !== 'string') return null
  const m = /^(\d{1,2}):(\d{2})$/.exec(s)
  if (!m) return null
  return Number(m[1]) * 60 + Number(m[2])
}

// weekly [{ dow, from, to }] → 7×24 boolean grid. An hour cell is available
// when any block covers it (floor(from) … ceil(to) at hour granularity).
export function weeklyToGrid(weekly) {
  const grid = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => false))
  for (const w of Array.isArray(weekly) ? weekly : []) {
    const dow = Number(w?.dow)
    if (!Number.isInteger(dow) || dow < 0 || dow > 6) continue
    const from = _hhmmToMinutes(w?.from)
    const to = _hhmmToMinutes(w?.to)
    if (from == null || to == null || to <= from) continue
    const row = dowToGridRow(dow)
    const startH = Math.max(0, Math.floor(from / 60))
    const endH = Math.min(24, Math.ceil(to / 60))
    for (let h = startH; h < endH; h++) grid[row][h] = true
  }
  return grid
}

export const weeklyHoursFromGrid = (grid) => {
  let count = 0
  for (let row = 0; row < 7; row++) {
    const cells = (grid && grid[row]) || []
    for (let h = 0; h < 24; h++) if (cells[h] === true) count++
  }
  return count
}

// The grid a person has actually DECLARED — explicit availabilityGrid first,
// else one derived from their workingHours window. Returns null when the
// person has set nothing (callers render an honest "No availability set"
// instead of inventing a schedule).
export function effectiveGrid(person) {
  const grid = person?.availabilityGrid
  if (Array.isArray(grid) && grid.length === 7) return grid
  const wh = person?.workingHours
  const start = _hhmmToMinutes(wh?.start)
  const end = _hhmmToMinutes(wh?.end)
  if (start == null || end == null) return null
  const out = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => false))
  for (let row = 0; row < 7; row++) {
    for (let h = 0; h < 24; h++) {
      const minute = h * 60
      out[row][h] = start < end ? minute >= start && minute < end : minute >= start || minute < end
    }
  }
  return out
}

// Current { row, hour } of `date` in the given IANA timezone (grid
// coordinates: row 0 = Mon). Falls back to the local timezone when `tz` is
// missing or invalid.
// `date` is defaulted in the BODY, not in the parameter list: frank's function
// emission mangles a default parameter whose value contains parentheses
// (`date = new Date()`), emitting a string that does not parse — so the
// handler would have been a dead string at runtime. Behaviour is identical for
// every caller. See tickets/fable.md.
export function tzGridPosition(tz, date) {
  date = date || new Date()
  let parts
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz || undefined,
      weekday: 'short',
      hour: 'numeric',
      hourCycle: 'h23'
    }).formatToParts(date)
  } catch {
    parts = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      hour: 'numeric',
      hourCycle: 'h23'
    }).formatToParts(date)
  }
  const weekday = parts.find((p) => p.type === 'weekday')?.value
  const hour = Number(parts.find((p) => p.type === 'hour')?.value)
  const row = AVAILABILITY_DAY_LABELS.indexOf(weekday)
  return { row: row < 0 ? 0 : row, hour: Number.isFinite(hour) ? hour % 24 : 0 }
}

// Schedule-derived status at `date`, evaluated in the person's timezone.
// This is DECLARED availability, not presence. Returns:
//   { availableNow: true, until: 'HH:00' | null }   — null until = 24/7 grid
//   { availableNow: false, next: { row, hour } | null }
// `date` is defaulted in the BODY, not in the parameter list: frank's function
// emission mangles a default parameter whose value contains parentheses
// (`date = new Date()`), emitting a string that does not parse — so the
// handler would have been a dead string at runtime. Behaviour is identical for
// every caller. See tickets/fable.md.
export function availabilityStatus(grid, tz, date) {
  date = date || new Date()
  if (!grid) return null
  const { row, hour } = tzGridPosition(tz, date)
  const at = (k) => {
    const idx = (row * 24 + hour + k) % 168
    const r = Math.floor(idx / 24)
    const h = idx % 24
    return { row: r, hour: h, on: !!(grid[r] && grid[r][h] === true) }
  }
  if (at(0).on) {
    let k = 1
    while (k < 168 && at(k).on) k++
    if (k >= 168) return { availableNow: true, until: null, untilHour: null }
    // `untilHour` is the DATA hour of the run's end; `until` stays its HH:00
    // text for callers that print it as it is.
    return { availableNow: true, until: _hh(at(k).hour), untilHour: at(k).hour }
  }
  let k = 1
  while (k < 168 && !at(k).on) k++
  if (k >= 168) return { availableNow: false, next: null }
  const n = at(k)
  return { availableNow: false, next: { row: n.row, hour: n.hour } }
}

// The summary's day names: the calendar's weekday keys (grid row 0 = Mon).
// Their ka values are ICU's short weekday forms, pinned by a test against
// `Intl.DateTimeFormat('ka', { weekday: 'short' })` in bun.
const _availabilitySummaryDayKeys = [
  'calendar.day.mon',
  'calendar.day.tue',
  'calendar.day.wed',
  'calendar.day.thu',
  'calendar.day.fri',
  'calendar.day.sat',
  'calendar.day.sun'
]

// One-line, tz-honest availability summary for read-only profile views.
// All times are in the PERSON's timezone (suffixed when known).
//
// Every word is copy from the bundles, so it answers through polyglot when
// the function runs BOUND to an element: `el.call('availabilitySummaryText',
// p)` binds `this` to the caller. The shared MemberProfile and the calendar
// ProfileModal both call it bound and render the answer as it is — it printed
// the raw `calendar.profile.noAvailability` (dev 8a4c8af39), and every other
// branch was English on a Georgian page. The weekly-hours unit is ICU's short
// `hour-per-week` held in the bundle ("{hours} hr/w", "{hours} სთ/კვრ"),
// never a runtime Intl call: Chrome ships no Georgian Intl data, so Intl
// would print English there. The IANA zone is data.
//
// The CLOCK is the calendar's own display clock — `formatTzTime`, reached
// through the element exactly as every calendar view reaches it with the
// session language: 12-hour in English ("9:00 AM"), h23 in Georgian
// ("09:00"). A grid hour is a wall-clock hour in the person's zone, so it is
// formatted as that hour of a UTC instant, in 'UTC'. It printed "09:00" in
// English while every other calendar view printed "9:00 AM".
//
// A plain call has no element to translate through, so it answers KEYS and
// the HH:00 clock — the no-grid branch answers exactly its key. The `now`
// default sits in the body: frank cannot emit a default parameter that holds
// parentheses (6291df9c1).
export function availabilitySummaryText(person, now) {
  now = now || new Date()
  const el = this && typeof this.call === 'function' ? this : null
  const t = (key) => (el ? el.call('polyglot', key) : key)
  const lang = el ? el.call('getActiveLang') : undefined
  const clock = (hour) => {
    const label = el ? el.call('formatTzTime', Date.UTC(2000, 0, 3, hour), 'UTC', lang) : null
    return typeof label === 'string' && label ? label : _hh(hour)
  }
  const grid = effectiveGrid(person)
  if (!grid) return t('calendar.profile.noAvailability')
  const tz = person?.timezone || null
  const status = availabilityStatus(grid, tz, now)
  const parts = []
  if (status.availableNow) {
    parts.push(t('calendar.profile.summary.availableNow'))
    if (status.untilHour != null) {
      parts.push(t('calendar.profile.summary.until').replace('{time}', clock(status.untilHour)))
    }
  } else if (status.next) {
    parts.push(
      t('calendar.profile.summary.nextAvailable')
        .replace('{day}', t(_availabilitySummaryDayKeys[status.next.row]))
        .replace('{time}', clock(status.next.hour))
    )
  } else {
    parts.push(t('calendar.profile.summary.noHours'))
    if (tz) parts.push(tz)
    return parts.join(' · ')
  }
  parts.push(
    t('calendar.profile.summary.weeklyHours').replace('{hours}', String(weeklyHoursFromGrid(grid)))
  )
  if (tz) parts.push(tz)
  return parts.join(' · ')
}
